import React, { useState, useEffect, useRef } from 'react';
import classNames from '../helper/classNames';
import { noop } from 'lodash';
import { useClickOutside } from '../helper/useClickOutside';
import { CrossIcon } from './icons/CrossIcon';

/**
 * A reusable tooltip component that can be positioned relative to any element
 *
 * @param {Object} props
 * @param {string} props.id - Unique ID for the tooltip (used for dismissal storage)
 * @param {string} props.content - The content to display in the tooltip
 * @param {string} props.position - The position of the tooltip relative to the target (top, bottom, left, right, topRight, bottomRight)
 * @param {boolean} props.show - Whether to show the tooltip
 * @param {Function} props.onDismiss - Callback when the tooltip is dismissed
 * @param {React.ReactNode} props.children - The element to attach the tooltip to
 * @param {string} props.className - Additional class names for the tooltip
 * @returns {React.ReactElement}
 */
export const Tooltip = ({
  id,
  content,
  position = 'bottom',
  show = true,
  onDismiss,
  children,
  className = '',
  onOutsideClick,
  style,
  buttonClassName = '',
  arrowClassName = '',
  dismissOnAnyKey = false,
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  useClickOutside(tooltipRef, onOutsideClick || noop);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  const handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsVisible(false);

    if (onDismiss) {
      onDismiss();
    }
  };

  // Stop event propagation to prevent parent elements' onClick handlers from firing
  const stopPropagation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Position classes based on the position prop
  const positionClasses = {
    top: 'bottom-full mb-2',
    topRight: 'bottom-full right-0 mb-2',
    topLeft: 'bottom-full left-full mb-2',
    bottom: 'top-full mt-2',
    bottomRight: 'top-full right-0 mt-2',
    left: 'right-full mr-2',
    right: 'left-full ml-2',
    leftTop: 'top-0 right-full mr-2',
  };

  // Arrow classes based on the position prop
  const arrowClasses = {
    top: 'bottom-[-8px] left-1/2 transform -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#151C71]',
    topRight:
      'bottom-[-8px] right-4 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#151C71]',
    topLeft:
      'bottom-[-8px] left-4 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-[#151C71]',
    bottom:
      'top-[-8px] left-1/2 transform -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-[#151C71]',
    bottomRight:
      'top-[-8px] right-4 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-[#151C71]',
    left: 'right-[-8px] top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-[#151C71]',
    right:
      'left-[-8px] top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-[#151C71]',
    leftTop:
      'right-[-8px] top-4 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-[#151C71]',
  };

  useEffect(() => {
    if (!dismissOnAnyKey) return;

    const handler = (event) => {
      if (onDismiss) {
        onDismiss();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dismissOnAnyKey, onDismiss]);

  return (
    <div className="relative inline-block" ref={containerRef}>
      {children}
      {isVisible && (
        <div
          style={{ ...style }}
          ref={tooltipRef}
          className={classNames(
            'absolute z-[49] w-64 p-4 bg-[#151C71] text-white rounded-md shadow-lg',
            positionClasses[position],
            className
          )}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          onTouchStart={stopPropagation}
        >
          <div
            className={classNames(
              'absolute w-0 h-0',
              arrowClasses[position],
              arrowClassName
            )}
          />
          {content}
          <button
            onClick={handleDismiss}
            className={classNames(
              'absolute -top-0 -right-7 text-red bg-[#101920] rounded-full w-5 h-5 flex items-center justify-center',
              buttonClassName
            )}
            aria-label="Close tooltip"
          >
            <CrossIcon className="fill-red" />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * A tooltip component
 *
 * @param {Object} props - All props from regular Tooltip plus:
 * @param {string} props.id - Required ID for storing dismissal state
 * @returns {React.ReactElement}
 */
export const PersistentTooltip = ({ id, ...props }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if this tooltip has been dismissed before
    const dismissedState = localStorage.getItem(`tooltip-${id}-dismissed`);
    if (dismissedState === 'true') {
      setIsDismissed(true);
    }
  }, [id]);

  const handleDismiss = (event) => {
    setIsDismissed(true);

    if (props.onDismiss) {
      props.onDismiss(event);
    }
  };

  if (isDismissed) {
    return props.children;
  }

  return <Tooltip {...props} onDismiss={handleDismiss} />;
};

export default Tooltip;
